'use client';

import { useWindowSizeStore } from 'src/stores/useWindowSizeStore';

/**
 * Returns a string representing the current window size. To use for conditional breakpoint rendering, do something like this:
 *
 * ['xs', 'sm'].includes(size)
 *
 * If you absolutely NEED to use the numerical values for width and height, you can get them from useWindowSizeStore directly.
 * However this will cause lots of rerenders when the window size changes.
 *
 * @returns {string} The current window size.
 */
const useWindowSizeString = () => {
	const size = useWindowSizeStore((s) => s.size);

	return size;
};

const sizeComparison = {
	xs: 0,
	sm: 1,
	md: 2,
	lg: 3,
	xl: 4,
	'2xl': 5,
};

/**
 * Returns true if window width is less than the threshold for the given size.
 *
 * Size thresholds (from getSizeString):
 * - 'xs': width < 744 (SMALL)
 * - 'sm': width < 1024 (MEDIUM)
 * - 'md': width < 1200 (LARGE)
 * - 'lg': width < 1440 (XLARGE)
 * - 'xl': width < 2560 (XXLARGE)
 * - '2xl': always false (nothing is greater than 2xl)
 *
 * @param size - The size threshold to compare against
 * @returns true if current window size is less than the given size
 */
export const useWindowSizeIsLessThan = (
	size: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
) => {
	const currentSize = useWindowSizeString();

	return sizeComparison[currentSize] < sizeComparison[size];
};

/**
 * Returns true if window width is greater than or equal to the next threshold.
 *
 * Size thresholds (from getSizeString):
 * - 'xs': width >= 744 (SMALL) - at least 'sm'
 * - 'sm': width >= 1024 (MEDIUM) - at least 'md'
 * - 'md': width >= 1200 (LARGE) - at least 'lg'
 * - 'lg': width >= 1440 (XLARGE) - at least 'xl'
 * - 'xl': width >= 2560 (XXLARGE) - at least '2xl'
 *
 * @param size - The size threshold to compare against
 * @returns true if current window size is greater than the given size
 */
export const useWindowSizeIsGreaterThan = (
	size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
) => {
	const currentSize = useWindowSizeString();

	return sizeComparison[currentSize] > sizeComparison[size];
};

const heightComparison = {
	short: 0,
	medium: 1,
	tall: 2,
};

/**
 * 'medium': <700px
 *
 * 'tall': <1000px
 *
 * @param height
 * @returns
 */
export const useWindowHeightIsLessThan = (height: 'medium' | 'tall') => {
	const currentHeight = useWindowSizeStore((s) => s.heightString);

	return heightComparison[currentHeight] < heightComparison[height];
};

/**
 * 'short': >=700px
 *
 * 'medium': >=1000px
 *
 * @param height
 * @returns
 */
export const useWindowHeightIsGreaterThan = (height: 'short' | 'medium') => {
	const currentHeight = useWindowSizeStore((s) => s.heightString);

	return heightComparison[currentHeight] > heightComparison[height];
};

export default useWindowSizeString;
